
import React, { useMemo } from 'react';
import Modal from 'react-modal';
import Button from '../../../Forms/Button';
import { StripePricingPlan } from '../../../../models/Stripe';
import { BillingAccount } from '../../../../models/BillingAccount';
import { numberToCurrencyString } from '../../../../services/formatting';

import './AccountPlans.confirm.scss';

interface AccountPlansConfirmProps {
    isOpen: boolean;
    fromPlan: StripePricingPlan;
    toPlan: StripePricingPlan;
    billingAccount: BillingAccount;
    onCancel: () => void;
    onConfirm: () => void;
}

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        border: 'none',
        borderRadius: '16px',
        boxShadow: '3px 4px 15px rgba(170, 170, 208, 0.5)',
        transform: 'translate(-50%, -50%)',
        overflow: 'visible',
    }
};
Modal.setAppElement('#root');

function AccountPlansConfirm({ isOpen, fromPlan, toPlan, billingAccount, onCancel, onConfirm }: AccountPlansConfirmProps) {

    const prorationData = useMemo(() => {
        if (!billingAccount?.hasStripeSubscription) return null;

        const { periodStartsAt, periodEndsAt } = billingAccount.stripeSubscriptionCfdiUsage;
        const now = Date.now();
        const totalDays = Math.round((periodEndsAt - periodStartsAt) / (1000 * 60 * 60 * 24));
        const remainingDays = Math.max(Math.ceil((periodEndsAt - now) / (1000 * 60 * 60 * 24)), 0);

        const newPlanCharge = (toPlan.unitAmountCents / 100) * (remainingDays / totalDays);
        const oldPlanCredit = (fromPlan.unitAmountCents / 100) * (remainingDays / totalDays);
        const netTotal = newPlanCharge - oldPlanCredit;

        return { remainingDays, newPlanCharge, oldPlanCredit, netTotal };
    }, [fromPlan, toPlan, billingAccount]);

    const isToMetered = toPlan?.isMeteredBilling;

    return (
        <Modal
            isOpen={isOpen}
            style={customStyles}
            onRequestClose={onCancel}
            shouldCloseOnEsc={true}
            shouldCloseOnOverlayClick={true}
            contentLabel="Cambio de Plan"
        >
            <div id="accountPlansConfirm">
                <h4>Cambio de Plan</h4>

                <p className="bodyText">
                    Tu ciclo mensual permanece sin cambios y el tope máximo de facturas se incrementa al plan seleccionado.
                </p>

                {isToMetered ? (
                    <p className="bodyText">
                        Al cambiar a un plan de facturación por consumo, <b>no se realizará ningún cobro en este momento</b>.
                        El monto de <b>${numberToCurrencyString(toPlan.unitAmountCents / 100)} MXN</b> se cobrará al finalizar tu ciclo actual de facturación.
                    </p>
                ) : (
                    <>
                        <p className="bodyText">
                            Se pagará una diferencia pro-rateada; al costo del nuevo plan seleccionado se le descontará lo proporcional a los días pendientes de tu plan actual.
                        </p>
                        {prorationData && (
                            <div className="prorationBreakdown">
                                <p className="prorationNote">Costo estimado, puede variar un poco</p>
                                <div className="prorationRow">
                                    <span className="label">{toPlan.name} ({prorationData.remainingDays} Días)</span>
                                    <span className="amount">${numberToCurrencyString(prorationData.newPlanCharge)} MXN</span>
                                </div>
                                <div className="prorationRow">
                                    <span className="label">{fromPlan.name} ({prorationData.remainingDays} Días)</span>
                                    <span className="amount deduction">– ${numberToCurrencyString(prorationData.oldPlanCredit)} MXN</span>
                                </div>
                                <div className="prorationTotal">
                                    <span className="label">Total al cambiar</span>
                                    <span className="amount">${numberToCurrencyString(prorationData.netTotal)} MXN</span>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div className="actionButtons">
                    <Button tertiary handleClick={onCancel} size='small'>
                        <span>Volver a mi cuenta</span>
                    </Button>
                    <Button primary handleClick={onConfirm} size='small'>
                        <span>Confirmar cambio</span>
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

export default AccountPlansConfirm;
